import { useMemo, type ReactNode } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { COPY } from "../../copy";
import type { P8ContinuedSalesPoint } from "../../p8OverviewData";
import { monthIndexFromLabel } from "../../p8OverviewData";
import type { AdsOrganizeBy, PrototypeVersion } from "../../types";
import { useCardPaddingClass } from "../CardDensity";

import {
  ChartHeadline,
  ChartHoverPanel,
  ChartLegend,
  ChartMonthAxisTick,
  ChartNavButtons,
  ChartTooltipExplore,
  ChartTooltipRows,
  CHART_LEGEND_GAP_CLASS,
  CHART_Y_AXIS_WIDTH,
  useChartMonthWindow,
  useChartPlotMargin,
  useChartPlotTopGapClass,
  useChartXAxisLabel,
  findNiceStepDollars,
  formatChartAxisCurrency,
  useChartHoverPanel,
  type ChartLegendItem,
  type ChartMonthWindowOptions,
  type ChartTooltipRow,
} from "./chartShared";

const IMMEDIATE_COLOR = "#2d2a70";
const CONTINUED_ACTUAL_COLOR = "#AFC1FF";
const SPEND_COLOR = "#F26532";

/**
 * Miniature of the stacked bar: ad sales on the bottom, first-year reorders on
 * top. Stands in for a single fill on the total, which is the full height of the
 * bar rather than one of its segments.
 */
function StackedBarSwatch() {
  return (
    <span
      className="relative h-2.5 w-2.5 shrink-0 overflow-hidden rounded-[2px]"
      aria-hidden
    >
      <span
        className="absolute inset-x-0 top-0 h-[5px]"
        style={{ backgroundColor: CONTINUED_ACTUAL_COLOR }}
      />
      <span
        className="absolute inset-x-0 bottom-0 h-[5px]"
        style={{ backgroundColor: IMMEDIATE_COLOR }}
      />
    </span>
  );
}

const Y_AXIS_TICK_COUNT = 5;
const BAR_TOP_RADIUS = 4;

type NoForecastSalesAndSpendChartProps = {
  version?: PrototypeVersion;
  organizeBy: AdsOrganizeBy;
  data: P8ContinuedSalesPoint[];
  onOpenMonth?: (monthIndex: number) => void;
  /** Rendered in the header, left of the month-paging arrows. */
  headerAction?: ReactNode;
  /** Paging for ranges holding more months than the axis shows at once. */
  monthWindow?: ChartMonthWindowOptions;
  /**
   * False on reorder ads, whose revenue all lands in the month the ad is
   * clicked. There is nothing that continues past it, so the chart reduces to
   * ad sales against ad spend.
   */
  showContinued?: boolean;
};

const IMMEDIATE_LEGEND_ITEM: ChartLegendItem = {
  key: "firstOrderSales",
  label: "Immediate sales",
  color: IMMEDIATE_COLOR,
  tooltip: COPY.overview.charts.actuals.immediate,
};

const CONTINUED_LEGEND_ITEM: ChartLegendItem = {
  key: "continuedSales",
  // No "(actual)" qualifier: this chart has no forecast segment to tell it
  // apart from, unlike the forecast-remainder cut of the same data.
  label: "Continued sales",
  color: CONTINUED_ACTUAL_COLOR,
  tooltip: COPY.overview.charts.actuals.continued,
};

const SPEND_LEGEND_ITEM: ChartLegendItem = {
  key: "adSpend",
  label: "Ad spend",
  color: SPEND_COLOR,
  tooltip: COPY.overview.charts.actuals.spend,
};

/** Continued sales cap the bar unless the cohort has none yet. */
function getTopSegmentKey(point: P8ContinuedSalesPoint): string {
  if (point.continuedSales > 0) {
    return "continuedSales";
  }
  return "firstOrderSales";
}

type BarShapeProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload?: P8ContinuedSalesPoint;
};

function createRoundedTopBar(dataKey: string) {
  return function RoundedTopBar({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    fill,
    payload,
  }: BarShapeProps) {
    if (width <= 0 || height <= 0) {
      return null;
    }
    const isTop = payload ? getTopSegmentKey(payload) === dataKey : false;
    const r = isTop ? Math.min(BAR_TOP_RADIUS, width / 2, height) : 0;
    const d = r
      ? `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${
          x + width - r
        },${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${
          y + height
        } Z`
      : `M${x},${y} L${x + width},${y} L${x + width},${y + height} L${x},${
          y + height
        } Z`;
    return <path d={d} fill={fill} />;
  };
}

function computeYAxisLayout(data: P8ContinuedSalesPoint[]) {
  const dataMax = Math.max(
    0,
    ...data.map((point) =>
      Math.max(point.firstOrderSales + point.continuedSales, point.adSpend)
    )
  );
  const target = Math.max(dataMax, 1000);
  const step = findNiceStepDollars(target / (Y_AXIS_TICK_COUNT - 1));
  const top = step * (Y_AXIS_TICK_COUNT - 1);
  const ticks = Array.from(
    { length: Y_AXIS_TICK_COUNT },
    (_, index) => index * step
  );
  return { domain: [0, top] as [number, number], ticks };
}

type SquareDotProps = { cx?: number; cy?: number; fill?: string };

function SquarePlotDot({ cx = 0, cy = 0, fill = "#333" }: SquareDotProps) {
  return (
    <rect
      x={cx - 3}
      y={cy - 3}
      width={6}
      height={6}
      rx={1}
      ry={1}
      fill={fill}
    />
  );
}

function ChartTooltip({
  label,
  point,
  showContinued,
  totalLabel,
  onExplore,
}: {
  label: string;
  point: P8ContinuedSalesPoint;
  showContinued: boolean;
  totalLabel: string;
  /** Absent when the chart has nowhere to drill into. */
  onExplore?: () => void;
}) {
  const immediate: ChartTooltipRow = {
    key: "firstOrderSales",
    label: "Immediate sales",
    value: point.firstOrderSales,
    depth: 0,
    swatch: { backgroundColor: IMMEDIATE_COLOR },
  };
  const spend: ChartTooltipRow = {
    key: "adSpend",
    label: "Ad spend",
    value: point.adSpend,
    depth: 0,
    swatch: { backgroundColor: SPEND_COLOR },
  };

  // Nothing is nested here: with the forecast gone, the two sales segments are
  // mutually exclusive parts of the total, so they all sit at one level. The
  // total comes after the segments it sums, reading bottom-up like the stack it
  // describes. Ad spend joins them above the rule rather than below it — it is
  // as measured as they are, and the rule now separates the figures from the
  // action. Without first-year reorders there is no total worth printing at all; it
  // would restate ad sales.
  const rows: ChartTooltipRow[] = showContinued
    ? [
        immediate,
        {
          key: "continuedSales",
          label: "Continued sales",
          value: point.continuedSales,
          depth: 0,
          swatch: { backgroundColor: CONTINUED_ACTUAL_COLOR },
        },
        {
          key: "total",
          label: totalLabel,
          value: point.firstOrderSales + point.continuedSales,
          depth: 0,
          swatchNode: <StackedBarSwatch />,
        },
        spend,
      ]
    : [immediate, spend];

  return (
    <div className="bg-fs-surface-primary rounded-md border border-[#dfe0e1] px-3 py-2 text-[14px] leading-[20px] text-[#333] shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
      <div className="mb-1 font-medium">{label}</div>
      <ChartTooltipRows rows={rows} />
      {onExplore ? <ChartTooltipExplore onClick={onExplore} /> : null}
    </div>
  );
}

/**
 * First-year returns with no forecast at all: only the revenue that has actually
 * landed, so every bar is a measured figure. Young cohorts therefore read short of
 * the first year they will eventually post — that gap is what the forecast cuts
 * of this chart fill in.
 *
 * The title sits on the card. V2 hangs the description off it as a tooltip;
 * V1 prints it underneath. Hovering a month still opens a panel with the
 * figures and an Explore action.
 */
export function NoForecastSalesAndSpendChart({
  version,
  organizeBy,
  data,
  onOpenMonth,
  headerAction,
  showContinued = true,
  monthWindow,
}: NoForecastSalesAndSpendChartProps) {
  const isOrderMonth = organizeBy === "order-month";
  const isV2 = version != null && version >= 2;
  const cardPaddingClass = useCardPaddingClass();
  const plotMargin = useChartPlotMargin();
  const plotTopGapClass = useChartPlotTopGapClass();
  const xAxisLabel = useChartXAxisLabel("Month");
  const {
    data: chartData,
    nav,
    slideRef,
  } = useChartMonthWindow(data, {
    pageable: monthWindow?.pageable ?? false,
    windowSize: monthWindow?.windowSize ?? data.length,
    resetKey: monthWindow?.resetKey ?? "",
  });
  // Scaled to every month the range holds, not just the visible window, so the
  // value axis stays put while paging moves the months underneath it.
  const { domain, ticks } = useMemo(() => computeYAxisLayout(data), [data]);
  const continuedLegendItem = {
    ...CONTINUED_LEGEND_ITEM,
    tooltip:
      version === 3
        ? COPY.overview.charts.actuals.v3Continued
        : isOrderMonth
          ? COPY.overview.charts.actuals.orderMonthContinued
          : COPY.overview.charts.actuals.continued,
  };
  const immediateLegendItem = {
    ...IMMEDIATE_LEGEND_ITEM,
    tooltip:
      version === 3
        ? COPY.overview.charts.actuals.v3Immediate
        : COPY.overview.charts.actuals.immediate,
  };
  const legendItems = showContinued
    ? [immediateLegendItem, continuedLegendItem, SPEND_LEGEND_ITEM]
    : [immediateLegendItem, SPEND_LEGEND_ITEM];

  const {
    hover,
    boundsRef,
    onChartMouseMove,
    onChartMouseLeave,
    onPanelEnter,
    onPanelLeave,
  } = useChartHoverPanel();
  const hoveredPoint = hover
    ? chartData.find((point) => point.month === hover.label)
    : undefined;

  return (
    <div
      className={`bg-fs-surface-primary flex h-full min-h-0 w-full flex-col rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
    >
      <ChartHeadline
        title={
          isV2
            ? version === 3
              ? COPY.overview.charts.actuals.v3Title
              : COPY.overview.charts.actuals.v2Title
            : isOrderMonth
              ? COPY.overview.charts.actuals.orderMonthTitle
              : COPY.overview.charts.actuals.title
        }
        {...(isV2
          ? {
              description: isOrderMonth
                ? COPY.overview.charts.actuals.orderMonthDescription
                : showContinued
                  ? COPY.overview.charts.actuals.description
                  : COPY.overview.charts.actuals.descriptionReorder,
            }
          : {
              subtitle: isOrderMonth
                ? COPY.overview.charts.actuals.orderMonthDescription
                : showContinued
                  ? COPY.overview.charts.actuals.description
                  : COPY.overview.charts.actuals.descriptionReorder,
            })}
        actions={
          <div className="flex items-center gap-3">
            {headerAction}
            <ChartNavButtons nav={nav} />
          </div>
        }
      />

      <div
        className={`${plotTopGapClass} flex min-h-0 flex-1 flex-col ${CHART_LEGEND_GAP_CLASS}`}
      >
        {/* Relative, so the hover panel can be positioned against the plot it
            describes while staying outside the Recharts wrapper. */}
        <div className="relative min-h-0 flex-1" ref={boundsRef}>
          <div className="h-full w-full overflow-hidden" ref={slideRef}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={plotMargin}
                barCategoryGap="30%"
                barSize={12}
                onMouseMove={onChartMouseMove}
                onMouseLeave={onChartMouseLeave}
              >
                <CartesianGrid
                  stroke="#DFE0E1"
                  strokeDasharray="3 0"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  tickLine={{ stroke: "#DFE0E1" }}
                  axisLine={{ stroke: "#DFE0E1" }}
                  interval={0}
                  height={44}
                  tick={(props) => <ChartMonthAxisTick {...props} />}
                  label={xAxisLabel}
                />
                <YAxis
                  domain={domain}
                  ticks={ticks}
                  interval={0}
                  tick={{ fontSize: 11, fill: "#757575" }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value: number) =>
                    formatChartAxisCurrency(value)
                  }
                  width={CHART_Y_AXIS_WIDTH}
                />
                {/* Kept only for the column highlight, which Recharts draws from
                  this element. The panel itself is rendered below, outside the
                  chart, so it can hold a button. */}
                <Tooltip
                  shared
                  cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                  content={() => null}
                />
                <Bar
                  dataKey="firstOrderSales"
                  stackId="sales"
                  fill={IMMEDIATE_COLOR}
                  shape={createRoundedTopBar("firstOrderSales")}
                  isAnimationActive={false}
                />
                {showContinued ? (
                  <Bar
                    dataKey="continuedSales"
                    stackId="sales"
                    fill={CONTINUED_ACTUAL_COLOR}
                    shape={createRoundedTopBar("continuedSales")}
                    isAnimationActive={false}
                  />
                ) : null}
                <Line
                  type="linear"
                  dataKey="adSpend"
                  stroke={SPEND_COLOR}
                  strokeWidth={1.5}
                  dot={(props: SquareDotProps) => (
                    <SquarePlotDot {...props} fill={SPEND_COLOR} />
                  )}
                  activeDot={(props: SquareDotProps) => (
                    <SquarePlotDot {...props} fill={SPEND_COLOR} />
                  )}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {hover && hoveredPoint ? (
            <ChartHoverPanel
              hover={hover}
              onMouseEnter={onPanelEnter}
              onMouseLeave={onPanelLeave}
            >
              <ChartTooltip
                label={hover.label}
                point={hoveredPoint}
                showContinued={showContinued}
                totalLabel={
                  isOrderMonth
                    ? version === 3
                      ? COPY.overview.charts.actuals.v3OrderMonthTotalLabel
                      : COPY.overview.charts.actuals.orderMonthTotalLabel
                    : COPY.overview.charts.actuals.totalLabel
                }
                onExplore={
                  onOpenMonth
                    ? () => onOpenMonth(monthIndexFromLabel(hover.label))
                    : undefined
                }
              />
            </ChartHoverPanel>
          ) : null}
        </div>

        <ChartLegend items={legendItems} />
      </div>
    </div>
  );
}
